import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import { useTranslation } from 'react-i18next';
import './LanguageSelectorDropdown.css';

const LanguageSelectorDropdown = ({ 
  id, 
  value, 
  onChange, 
  label, 
  placeholder = "Select language", 
  isSource = true 
}) => {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  // Function to fetch available languages
  const fetchLanguages = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.translations.getLanguages();
      
      // Filter languages based on whether this is a source or target selector
      // and only include active languages
      const filteredLanguages = response.data.filter(lang => {
        return lang.is_active && (
          isSource ? lang.available_as_source : lang.available_as_target
        );
      });
      
      setLanguages(filteredLanguages);
    } catch (err) {
      setError('Failed to fetch languages');
      console.error('Error fetching languages:', err);
    } finally {
      setLoading(false);
    }
  }, [isSource]);

  // Fetch available languages on component mount
  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  // Handle language selection change
  const handleChange = (e) => {
    const selectedIndex = e.target.selectedIndex;
    if (selectedIndex > 0 && languages[selectedIndex - 1]) {
      const selectedLanguage = languages[selectedIndex - 1];
      // Create a custom event object that includes the language ID
      const customEvent = {
        ...e,
        target: {
          ...e.target,
          value: e.target.value,
          selectedLanguage: selectedLanguage
        }
      };
      onChange(customEvent);
    } else {
      onChange(e);
    }
  };

  return (
    <div className={`language-input ${isRTL ? 'rtl' : 'ltr'}`}>
      {label && <label htmlFor={id}>{label}</label>}
      <select
        id={id}
        value={value}
        onChange={handleChange}
        disabled={loading || languages.length === 0}
        className={isRTL ? 'rtl-select' : 'ltr-select'}
      >
        <option value="" disabled>{loading ? 'Loading...' : placeholder}</option>
        {languages.map(language => (
          <option 
            key={language.id} 
            value={language.code}
            data-language-id={language.id}
          >
            {language.name}
          </option>
        ))}
      </select>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default LanguageSelectorDropdown; 